import type { ImportProduct } from './import-types';
import { IMPORT_PRODUCTS, getImportEntity } from './import-data';

export type Severity = 'info' | 'warning' | 'danger' | 'success';

export interface DutyLine {
  subCode: string;
  label: string;
  ratePct: number;
  cost: number;
  note?: string;
}

export interface CostSummary {
  baseRatePct: number;
  baseCost: number;
  additionalRatePct: number;
  additionalCost: number;
  totalDutyCost: number;
  totalCost: number;
  unitValue: number;
}

export interface FtaItem {
  program: string;
  status: 'Free' | 'Reduced' | 'Not eligible';
  note?: string;
}

export interface ExclusionItem {
  subCode: string;
  label: string;
  ratePct: number;
  applicability: 'applicable' | 'candidate' | 'not-applicable';
  note?: string;
}

export interface RiskFlag {
  label: string;
  severity: Severity;
  note: string;
}

export interface HtsHistoryEntry {
  date: string;
  change: string;
}

export interface CrossRuling {
  id: string;
  title: string;
  summary: string;
  date: string;
  applicability: number; // 0-100, higher = more applicable
  url: string;
}

export interface BiteLensGroup {
  name: string;
  total: number;
  newCount: number;
  columns: string[];
  rows: Record<string, string | number>[];
}

export interface PgaDetail {
  agency: string;
  flagged: boolean;
  program?: string;
  note: string;
}

export interface HtsClassification {
  code: string;
  confidence: number; // 0-100
  description: string;
  reasoning: string;
  alternates: { code: string; reason: string }[];
}

export interface AiSummary {
  risk: 'Low' | 'Medium' | 'High' | 'Critical';
  burden: 'Low' | 'Medium' | 'High' | null;
  keyFactors: string[];
  narrative: string;
  status?: 'available' | 'unavailable' | 'error';
  productName?: string;
  bestMatch?: string;
  matchProbability?: number | null;
  screenedAt?: string;
  topMatches?: {
    id?: string;
    name: string;
    confidence: number;
  }[];
  riskAssessment?: {
    overallRisk?: string;
    rationale?: string;
    highLists?: string[];
    perMatch?: {
      docId?: string;
      risk?: string;
      rulesTriggered: string[];
    }[];
  };
  error?: string;
}

export interface ProductDoc {
  id: string;
  name: string;
  type: string;
  date: string;
  size: string;
}

export interface ProductComment {
  id: string;
  author: string;
  time: string;
  text: string;
}

export interface TariffInputs {
  smeltCountry: string;
  copperPct: number;
  copperValue: number;
  aluminumPct: number;
  aluminumValue: number;
  steelPct: number;
  steelValue: number;
  hierarchyType: string;
  category: string;
  manufacturer: string;
  vendor: string;
}

export interface CoBreakdownItem {
  country: string;
  percent: number;
}

export interface ChildComponent {
  name: string;
  hts: string;
  percent: number;
  origin: string;
}

export interface ProductDetail {
  ai: AiSummary;
  classification: HtsClassification;
  pga: PgaDetail[];
  forcedLabor: {
    result: string;
    basis: string;
    entities: string[];
    severity: Severity;
    groups?: BiteLensGroup[];
  };
  ftaDetail: {
    program: string;
    eligible: boolean;
    reasoning: string;
    criteria: string[];
  };
  adcvd: {
    exposure: boolean;
    cases: {
      caseNumber: string;
      product: string;
      // Null when the source has no deposit rate (screening hits).
      rate: number | null;
      producer: string;
      country: string;
    }[];
    scopeDetermination: string;
    reasoning: string;
    severity: Severity;
    groups?: BiteLensGroup[];
  };
  tariffReasoning: string;
  dutyLines: DutyLine[];
  cost: CostSummary;
  ftas: FtaItem[];
  exclusions: ExclusionItem[];
  riskFlags: RiskFlag[];
  htsHistory: HtsHistoryEntry[];
  crossRulings: CrossRuling[];
  biteLensGroups: BiteLensGroup[];
  screeningLensGroups: BiteLensGroup[];
  // EU-specific and uncategorized Program Codes hits. Both stay empty for
  // mock products; real values come from stored or live screening results.
  euImports: BiteLensGroup[];
  other: BiteLensGroup[];
  coBreakdown: CoBreakdownItem[];
  childComponents: ChildComponent[];
  tariffInputs: TariffInputs;
  documents: ProductDoc[];
  comments: ProductComment[];
  deniedPartyMatches: {
    entity: string;
    list: string;
    date: string;
    agency?: string;
    country?: string;
    sourceUrl?: string;
    source?: string;
    score?: string;
  }[];
}

const ALT_BY_CHAPTER: Record<string, { code: string; reason: string }[]> = {
  '85': [
    { code: '8504.40.95', reason: 'If primary function is power conversion' },
    { code: '8543.70.99', reason: 'Other electrical machines NES' },
  ],
  '62': [
    { code: '6203.42.40', reason: "Men's trousers analogue" },
    { code: '6211.42.10', reason: 'If classified as garment NES' },
  ],
  '84': [
    { code: '8479.89.94', reason: 'Other machines with individual functions' },
  ],
  '29': [{ code: '2933.99.79', reason: 'Heterocyclic compounds NES' }],
  '87': [{ code: '8708.99.81', reason: 'Other parts of motor vehicles' }],
};

function alts(hts: string) {
  const ch = hts.slice(0, 2);
  return (
    ALT_BY_CHAPTER[ch] ?? [
      { code: `${ch}.00.99`, reason: 'Residual basket within chapter' },
    ]
  );
}

const PGA_NOTES: Record<string, string> = {
  FDA: 'Prior notice + FDA review at entry.',
  EPA: 'TSCA / FIFRA filing required.',
  USDA: 'APHIS permit verification.',
  FWS: 'ESA / CITES screening.',
  CPSC: "Children's product certificate or general conformity required.",
  TSCA: 'Section 13 import certification.',
  DOT: 'Hazmat / FMVSS conformity required.',
};

function pgaList(product: ImportProduct): PgaDetail[] {
  const ALL = ['FDA', 'EPA', 'USDA', 'FWS', 'CPSC', 'TSCA', 'DOT'];
  return ALL.map((a) => {
    const flagged = (product.pgaFlags as string[]).includes(a);
    return {
      agency: a,
      flagged,
      program: flagged ? a + ' standard review' : undefined,
      note: flagged ? PGA_NOTES[a] ?? 'Review required.' : 'No flag.',
    };
  });
}

function buildBiteGroups(p: ImportProduct): BiteLensGroup[] {
  const groups: BiteLensGroup[] = [];

  // HTS Codes lens — always present
  groups.push({
    name: 'Nomenclatures – HTS Codes Schedule B and CN Codes',
    total: 3,
    newCount: 1,
    columns: ['HS Code', 'Description', 'Source', 'Date Added'],
    rows: [
      {
        'HS Code': p.htsCode || '—',
        Description: p.name,
        Source: 'USITC HTS 2025',
        'Date Added': '2025-02-14',
      },
      {
        'HS Code': (p.htsCode || '0000').slice(0, 4) + '.00.00',
        Description: 'Heading-level match',
        Source: 'USITC HTS 2025',
        'Date Added': '2024-11-02',
      },
      {
        'HS Code': (p.htsCode || '0000').slice(0, 2) + '00',
        Description: 'Chapter-level rollup',
        Source: 'Schedule B',
        'Date Added': '2024-08-19',
      },
    ],
  });

  // US Duties & Tariffs lens — present if any tariff exposure
  if (p.section301 || p.section232 || p.additionalDutyPct > 0) {
    const rows: Record<string, string | number>[] = [];
    if (p.section301)
      rows.push({
        Program: 'Section 301 List 4A',
        Rate: `${p.section301Rate}%`,
        Origin: p.countryOfOrigin,
        'Date Added': '2024-09-27',
      });
    if (p.section232)
      rows.push({
        Program: 'Section 232 Steel/Aluminum',
        Rate: `${p.section232Rate}%`,
        Origin: p.countryOfOrigin,
        'Date Added': '2024-06-01',
      });
    if (p.additionalDutyPct > 0)
      rows.push({
        Program: 'IEEPA Additional Duty',
        Rate: `${p.additionalDutyPct}%`,
        Origin: p.countryOfOrigin,
        'Date Added': '2025-03-04',
      });
    groups.push({
      name: 'US Duties & Tariffs',
      total: rows.length,
      newCount: 1,
      columns: ['Program', 'Rate', 'Origin', 'Date Added'],
      rows,
    });
  }

  // Forced labor lens — present if UFLPA > none or origin China/Xinjiang
  if (p.uflpaRisk !== 'none') {
    const supplier = getImportEntity(p.supplierId);
    groups.push({
      name: 'Forced Labor Commodities',
      total: p.uflpaRisk === 'high' ? 4 : 2,
      newCount: p.uflpaRisk === 'high' ? 2 : 0,
      columns: ['Entity', 'Country', 'Agency', 'List', 'Date Added'],
      rows: [
        {
          Entity: supplier?.name ?? 'Supplier',
          Country: supplier?.country ?? p.countryOfOrigin,
          Agency: 'DHS',
          List: 'UFLPA Entity List',
          'Date Added': '2024-12-08',
        },
        {
          Entity: 'Upstream cotton mill (Xinjiang)',
          Country: 'China',
          Agency: 'CBP',
          List: 'WRO 2020-052',
          'Date Added': '2024-07-12',
        },
        ...(p.uflpaRisk === 'high'
          ? [
              {
                Entity: 'Polysilicon refiner XJ-04',
                Country: 'China',
                Agency: 'DHS',
                List: 'UFLPA Strategy Annex',
                'Date Added': '2025-01-09',
              },
              {
                Entity: 'Logistics handler (XPCC)',
                Country: 'China',
                Agency: 'OFAC',
                List: 'SDN List',
                'Date Added': '2024-11-22',
              },
            ]
          : []),
      ],
    });
  }

  // PGA lens — present if PGA flags
  if (p.pgaFlags.length) {
    groups.push({
      name: 'Partner Government Agency Filings',
      total: p.pgaFlags.length,
      newCount: 0,
      columns: ['Agency', 'Program', 'Filing', 'Date Added'],
      rows: p.pgaFlags.map((a) => ({
        Agency: a,
        Program: PGA_NOTES[a] ?? 'Review',
        Filing: `${a}-PN`,
        'Date Added': '2024-10-04',
      })),
    });
  }

  return groups;
}

function dutyLines(
  p: ImportProduct,
  baseRate: number,
  value: number,
): DutyLine[] {
  const lines: DutyLine[] = [];
  if (baseRate > 0)
    lines.push({
      subCode: p.htsCode,
      label: 'Base Duty (MFN)',
      ratePct: baseRate,
      cost: value * (baseRate / 100),
    });
  if (p.section301)
    lines.push({
      subCode: '9903.88.03',
      label: 'Section 301 List 4A',
      ratePct: p.section301Rate,
      cost: value * (p.section301Rate / 100),
      note: 'China-origin remedy',
    });
  if (p.section232)
    lines.push({
      subCode: '9903.81.01',
      label: 'Section 232 (Steel/Aluminum)',
      ratePct: p.section232Rate,
      cost: value * (p.section232Rate / 100),
    });
  if (p.additionalDutyPct > 0)
    lines.push({
      subCode: '9903.01.24',
      label: 'Temporary Section 122 Additional Duty',
      ratePct: p.additionalDutyPct,
      cost: value * (p.additionalDutyPct / 100),
      note: 'Tariff-rate response measure',
    });
  if (lines.length === 0)
    lines.push({
      subCode: p.htsCode,
      label: 'Base Duty (MFN)',
      ratePct: 0,
      cost: 0,
      note: 'Duty-free MFN',
    });
  return lines;
}

export function buildProductDetail(p: ImportProduct): ProductDetail {
  const value = p.declaredValue;
  // Synthetic base MFN rate from compliance score (deterministic, demo only)
  const baseRate = p.htsCode
    ? p.complianceScore < 50
      ? 3.5
      : p.complianceScore < 80
      ? 2.5
      : 0
    : 0;
  const lines = dutyLines(p, baseRate, value);
  const baseCost = lines
    .filter((l) => l.label.startsWith('Base'))
    .reduce((s, l) => s + l.cost, 0);
  const addCost = lines
    .filter((l) => !l.label.startsWith('Base'))
    .reduce((s, l) => s + l.cost, 0);
  const totalDuty = baseCost + addCost;
  const additionalRatePct = lines
    .filter((l) => !l.label.startsWith('Base'))
    .reduce((s, l) => s + l.ratePct, 0);

  const supplier = getImportEntity(p.supplierId);

  const isAuto = p.importDatabaseId === 'idb-auto';
  const isChem = p.importDatabaseId === 'idb-chem';
  const isMch = p.importDatabaseId === 'idb-machinery';

  return {
    ai: {
      risk: p.riskLevel,
      burden:
        p.pgaFlags.length >= 2 || p.uflpaRisk === 'high'
          ? 'High'
          : p.pgaFlags.length || totalDuty > 5000
          ? 'Medium'
          : 'Low',
      keyFactors: [
        p.section301
          ? `Section 301 List 4A applies at ${p.section301Rate}%`
          : null,
        p.section232 ? `Section 232 metals duty at ${p.section232Rate}%` : null,
        p.additionalDutyPct > 0
          ? `Temporary additional duty at ${p.additionalDutyPct}%`
          : null,
        p.uflpaRisk !== 'none'
          ? `UFLPA risk: ${p.uflpaRisk.toUpperCase()}`
          : null,
        p.pgaFlags.length ? `PGA review by ${p.pgaFlags.join(', ')}` : null,
        !p.htsCode
          ? 'HTS code missing — classification required before entry'
          : null,
        !p.countryOfOrigin ? 'Country of origin not declared' : null,
        p.ftaEligible ? `Eligible under ${p.fta}` : null,
      ].filter(Boolean) as string[],
      narrative:
        p.uflpaRisk === 'high'
          ? `High-risk import: supplier ${
              supplier?.name ?? ''
            } maintains upstream ties to entities on the UFLPA Entity List. Recommend full supply-chain trace, supplier audit, and documented rebuttal evidence before scheduling entry. Detention probability is elevated.`
          : p.pgaFlags.length
          ? `Subject to ${p.pgaFlags.join(
              ' / ',
            )} review at entry. Confirm filings are staged in ACE prior to arrival to avoid hold. Tariff exposure is ${
              totalDuty > 0 ? 'non-trivial' : 'limited'
            }; classification is consistent with the declared description.`
          : p.ftaEligible
          ? `Low-risk entry. Eligible under ${p.fta} subject to rules-of-origin documentation. Maintain certification on file. No PGA or forced-labor exposure detected.`
          : `Routine entry profile. Verify the declared HTS against actual specifications, confirm broker filings reflect any additional-duty exposure, and retain origin documentation per CBP recordkeeping rules.`,
    },
    classification: {
      code: p.htsCode || 'Unclassified',
      confidence: p.htsCode
        ? Math.max(45, Math.min(98, p.complianceScore + 8))
        : 0,
      description: p.htsCode
        ? `${p.name}. Classified at the 10-digit HTSUS level based on declared materials, function, and end use.`
        : 'No HTS code on file. Product description and material composition are insufficient for automated classification — manual ruling recommended.',
      reasoning: p.htsCode
        ? `Principal function and material composition align to heading ${p.htsCode.slice(
            0,
            4,
          )}. Subheading selection driven by intended end-use and the technical specification supplied by ${
            supplier?.name ?? 'the supplier'
          }.`
        : 'Classification cannot be completed automatically. Submit a CBP binding ruling request or supply complete product specifications.',
      alternates: p.htsCode ? alts(p.htsCode) : [],
    },
    pga: pgaList(p),
    forcedLabor: {
      result:
        p.uflpaRisk === 'high'
          ? 'High risk — UFLPA Entity List match'
          : p.uflpaRisk === 'medium'
          ? 'Medium risk — adjacent supply chain'
          : p.uflpaRisk === 'low'
          ? 'Low risk — monitor'
          : 'No exposure detected',
      basis:
        p.uflpaRisk === 'high'
          ? 'Origin: China. Upstream cotton/polysilicon sourcing traced to Xinjiang. Rebuttable presumption applies.'
          : p.uflpaRisk === 'medium'
          ? 'Origin or component sourcing within elevated-risk corridor; documentation review recommended.'
          : p.uflpaRisk === 'low'
          ? 'Adjacent risk corridor; periodic supplier attestation sufficient.'
          : 'No origin or supplier intersection with UFLPA Entity List or active WROs.',
      entities:
        p.uflpaRisk === 'high'
          ? [
              supplier?.name ?? 'Supplier',
              'Upstream cotton mill (Xinjiang)',
              'Polysilicon refiner XJ-04',
            ]
          : p.uflpaRisk !== 'none'
          ? [supplier?.name ?? 'Supplier']
          : [],
      severity:
        p.uflpaRisk === 'high'
          ? 'danger'
          : p.uflpaRisk === 'medium'
          ? 'warning'
          : p.uflpaRisk === 'low'
          ? 'info'
          : 'success',
    },
    ftaDetail: {
      program: p.fta,
      eligible: p.ftaEligible,
      reasoning: p.ftaEligible
        ? `Meets ${p.fta} rules-of-origin: substantial transformation occurred in ${p.countryOfOrigin}; regional value content threshold satisfied.`
        : p.fta !== 'none'
        ? `${p.fta} declared but rules-of-origin evidence is incomplete. Obtain producer certification before claiming preference.`
        : 'No preferential program declared. Confirm whether the product qualifies under any current US FTA.',
      criteria: p.ftaEligible
        ? [
            'Wholly obtained / produced',
            'Tariff shift met at HS heading',
            'Regional value content ≥ threshold',
            'Direct shipment from territory',
          ]
        : [
            'Producer certification required',
            'Rules-of-origin evidence missing',
            'Tariff shift not yet validated',
          ],
    },
    adcvd: ((): ProductDetail['adcvd'] => {
      const exposed =
        p.id === 'ip-405' ||
        isChem ||
        (p.section232 &&
          (p.htsCode?.startsWith('72') ||
            p.htsCode?.startsWith('73') ||
            p.htsCode?.startsWith('76')));
      if (!exposed) {
        return {
          exposure: false,
          cases: [],
          scopeDetermination:
            'Out of scope — no active AD/CVD order applies to this HTS / origin combination.',
          reasoning:
            'Cross-checked active AD and CVD orders published by Commerce / ITC against the declared HTS and country of origin. No match found.',
          severity: 'success',
        };
      }
      const country = p.countryOfOrigin || 'China';
      const cases = isChem
        ? [
            {
              caseNumber: 'A-570-893',
              product: 'Active Pharmaceutical Ingredient',
              rate: 82.36,
              producer: supplier?.name ?? 'Producer',
              country,
            },
            {
              caseNumber: 'C-570-894',
              product: 'API — countervailing',
              rate: 12.44,
              producer: supplier?.name ?? 'Producer',
              country,
            },
          ]
        : p.htsCode?.startsWith('76')
        ? [
            {
              caseNumber: 'A-570-073',
              product: 'Common alloy aluminum sheet',
              rate: 96.3,
              producer: supplier?.name ?? 'Producer',
              country,
            },
          ]
        : p.htsCode?.startsWith('72') || p.htsCode?.startsWith('73')
        ? [
            {
              caseNumber: 'A-570-026',
              product: 'Corrosion-resistant steel',
              rate: 199.43,
              producer: supplier?.name ?? 'Producer',
              country,
            },
            {
              caseNumber: 'C-570-027',
              product: 'Corrosion-resistant steel — CVD',
              rate: 39.05,
              producer: supplier?.name ?? 'Producer',
              country,
            },
          ]
        : [
            {
              caseNumber: 'A-570-106',
              product: 'Catalytic converter substrate',
              rate: 47.81,
              producer: supplier?.name ?? 'Producer',
              country,
            },
          ];
      return {
        exposure: true,
        cases,
        scopeDetermination: `Within scope of ${cases.length} active order${
          cases.length > 1 ? 's' : ''
        }. Producer ${
          supplier?.name ?? '—'
        } is not on the company-specific exclusion list; the all-others rate applies.`,
        reasoning: `HTS ${p.htsCode} from ${country} matches the product description and country named in the order. Cash deposit is required at entry equal to the listed rate(s); final liquidation depends on the next administrative review.`,
        severity: cases.some((c) => c.rate > 50) ? 'danger' : 'warning',
      };
    })(),
    tariffReasoning:
      p.section301 || p.section232 || p.additionalDutyPct > 0
        ? `Origin ${p.countryOfOrigin} triggers ${[
            p.section301 ? 'Section 301' : null,
            p.section232 ? 'Section 232' : null,
            p.additionalDutyPct > 0 ? 'additional IEEPA duty' : null,
          ]
            .filter(Boolean)
            .join(', ')} on heading ${p.htsCode.slice(0, 4) || '—'}.`
        : 'No additional remedy duties apply to this origin / heading combination.',
    dutyLines: lines,
    cost: {
      baseRatePct: baseRate,
      baseCost,
      additionalRatePct,
      additionalCost: addCost,
      totalDutyCost: totalDuty,
      totalCost: value + totalDuty,
      unitValue: value,
    },
    ftas: [
      isAuto
        ? {
            program: 'USMCA',
            status: p.ftaEligible
              ? ('Free' as const)
              : ('Not eligible' as const),
            note: 'Auto parts annex',
          }
        : p.ftaEligible
        ? { program: p.fta, status: 'Free' as const }
        : {
            program: 'Civil Aircraft Agreement',
            status: 'Free' as const,
            note: 'If end use is civil aviation',
          },
      {
        program: 'GSP',
        status: 'Not eligible' as const,
        note: 'GSP lapsed for this origin',
      },
      {
        program: 'WTO Pharmaceutical',
        status: isChem ? 'Free' : 'Not eligible',
        note: isChem ? 'Listed API' : 'Out of scope',
      },
    ],
    exclusions:
      p.additionalDutyPct > 0 || p.section232 || p.section301
        ? [
            {
              subCode: '9903.01.32',
              label: 'Section 122 — humanitarian / informational materials',
              ratePct: 0,
              applicability: 'not-applicable' as const,
              note: 'End-use does not qualify',
            },
            ...(isMch
              ? [
                  {
                    subCode: '9903.81.95',
                    label: 'Section 232 MHDV repair / replacement',
                    ratePct: 0,
                    applicability: 'candidate' as const,
                    note: 'Confirm replacement use in qualifying vehicle',
                  },
                ]
              : []),
            ...(p.section301
              ? [
                  {
                    subCode: '9903.88.67',
                    label: 'Section 301 product-specific exclusion (machinery)',
                    ratePct: 0,
                    applicability: 'candidate' as const,
                    note: 'Compare technical spec to exclusion language',
                  },
                ]
              : []),
          ]
        : [],
    riskFlags: [
      ...(p.uflpaRisk !== 'none'
        ? [
            {
              label: 'Forced Labor — UFLPA / WRO exposure',
              severity:
                p.uflpaRisk === 'high'
                  ? ('danger' as const)
                  : ('warning' as const),
              note:
                p.uflpaRisk === 'high'
                  ? 'Rebuttable presumption applies. Detention probability elevated.'
                  : 'Upstream supply-chain documentation required.',
            },
          ]
        : []),
      ...(!p.htsCode
        ? [
            {
              label: 'HTS classification missing',
              severity: 'danger' as const,
              note: 'Cannot file entry without a valid 10-digit HTS.',
            },
          ]
        : []),
      ...(!p.countryOfOrigin
        ? [
            {
              label: 'Country of origin not declared',
              severity: 'warning' as const,
              note: 'Required for tariff and FTA determination.',
            },
          ]
        : []),
    ],
    htsHistory: p.htsCode
      ? [
          {
            date: '2024-09-27',
            change: 'Section 301 List 4A modification — rate confirmed at 25%',
          },
          {
            date: '2024-01-01',
            change: 'HTS 2024 revision — no change to this 10-digit subheading',
          },
        ]
      : [],
    // CROSS rulings are generated on demand and cached on DatabaseProduct.
    // Never seed this section with demo results for a real product.
    crossRulings: [],
    biteLensGroups: buildBiteGroups(p),
    screeningLensGroups: buildBiteGroups(p),
    euImports: [],
    other: [],
    coBreakdown: p.countryOfOrigin
      ? p.uflpaRisk === 'high'
        ? [
            { country: p.countryOfOrigin, percent: 72 },
            { country: 'Vietnam', percent: 18 },
            { country: 'Bangladesh', percent: 10 },
          ]
        : [
            { country: p.countryOfOrigin, percent: 88 },
            { country: 'United States', percent: 12 },
          ]
      : [],
    childComponents:
      isMch || p.id === 'ip-102'
        ? [
            {
              name: 'Primary subassembly',
              hts: (p.htsCode || '0000.00.00').slice(0, 4) + '.10.00',
              percent: 60,
              origin: p.countryOfOrigin || '—',
            },
            {
              name: 'Fastener / hardware kit',
              hts: '7318.15.80',
              percent: 12,
              origin: 'Taiwan',
            },
            {
              name: 'Electronic control module',
              hts: '8537.10.91',
              percent: 28,
              origin: 'South Korea',
            },
          ]
        : [],
    tariffInputs: {
      smeltCountry: p.section232 ? p.countryOfOrigin : 'N/A',
      copperPct: 0,
      copperValue: 0,
      aluminumPct: p.section232 && p.htsCode.startsWith('76') ? 100 : 0,
      aluminumValue: p.section232 && p.htsCode.startsWith('76') ? value : 0,
      steelPct:
        p.section232 &&
        (p.htsCode.startsWith('72') || p.htsCode.startsWith('73'))
          ? 100
          : 0,
      steelValue:
        p.section232 &&
        (p.htsCode.startsWith('72') || p.htsCode.startsWith('73'))
          ? value
          : 0,
      hierarchyType: isMch ? 'Assembly' : 'Finished good',
      category: isChem
        ? 'Chemical'
        : isAuto
        ? 'Automotive part'
        : isMch
        ? 'Machinery'
        : p.importDatabaseId === 'idb-apparel'
        ? 'Apparel'
        : 'Electronics',
      manufacturer: getImportEntity(p.manufacturerId)?.name ?? '—',
      vendor: supplier?.name ?? '—',
    },
    documents:
      p.id === 'ip-102'
        ? [
            {
              id: 'd1',
              name: 'Commercial_invoice.pdf',
              type: 'Invoice',
              date: '2025-03-08',
              size: '184 KB',
            },
            {
              id: 'd2',
              name: 'Packing_list.pdf',
              type: 'Packing list',
              date: '2025-03-08',
              size: '62 KB',
            },
            {
              id: 'd3',
              name: 'UFLPA_due_diligence.pdf',
              type: 'Compliance',
              date: '2025-03-02',
              size: '1.4 MB',
            },
          ]
        : p.pgaFlags.length
        ? [
            {
              id: 'd1',
              name: 'Commercial_invoice.pdf',
              type: 'Invoice',
              date: '2025-03-04',
              size: '172 KB',
            },
            {
              id: 'd2',
              name: `${p.pgaFlags[0]}_filing.xml`,
              type: 'PGA filing',
              date: '2025-03-04',
              size: '28 KB',
            },
          ]
        : [],
    comments:
      p.id === 'ip-102'
        ? [
            {
              id: 'c1',
              author: 'M. Alvarez',
              time: '2 days ago',
              text: 'Holding entry pending UFLPA documentation package from supplier.',
            },
            {
              id: 'c2',
              author: 'K. Chen',
              time: 'yesterday',
              text: 'Requested cotton-to-bale supply chain trace; expected by Friday.',
            },
          ]
        : [],
    deniedPartyMatches:
      p.uflpaRisk === 'high'
        ? [
            {
              entity: supplier?.name ?? 'Supplier',
              list: 'UFLPA Entity List',
              date: '2024-12-08',
            },
          ]
        : [],
  };
}

export function getImportProductDetail(id: string) {
  const p = IMPORT_PRODUCTS.find((x) => x.id === id);
  if (!p) return null;
  return { product: p, detail: buildProductDetail(p) };
}
